import useAuth from 'hooks/useAuth';
import { toast } from 'react-toastify';
import { Row, Col, Card } from 'react-bootstrap';
import baseController from 'api/base.controller';
import Error401 from 'components/pages/errors/Error401';
import ReloadButton from '../sistema/reloadButton';
import InsertButton from '../sistema/insertButton';
import ActionsColumn from '../sistema/actionsColumn';
import { cardapioRoles } from '../sistema/allowedRoles';
import React, { useEffect, useState } from 'react';
import FullScreenLoading from '../sistema/fullScreenLoading';
import CalculateRowQuantity from '../sistema/calculateRowQuantity';
import AdvanceTable from '../../common/advance-table/AdvanceTable';
import { emptyMessage, errorMessage } from '../sistema/gridMessages';
import AdvanceTableWrapper from '../../common/advance-table/AdvanceTableWrapper';
import AdvanceTableSearchBox from '../../common/advance-table/AdvanceTableSearchBox';
import AdvanceTablePagination from '../../common/advance-table/AdvanceTablePagination';

export default function Cardapio() {
  let timeout;
  window.onresize = () => {
    clearTimeout(timeout);

    timeout = setTimeout(() => {
      CalculateRowQuantity(itemsPerPage, setItemsPerPage)
    }, 500);
  };

  const { auth } = useAuth();
  const [page, setPage] = useState(1);
  const [data, setData] = useState([]);
  const [isSlow, setIsSlow] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [authorized, setAuthorized] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(0);
  const [globalFilter, setGlobalFilter] = useState("");
  const [message, setMessage] = useState(emptyMessage);

  const goToPage = (page) => {
    setPage(page)
  };

  const columns = [
    {
      accessor: 'nome',
      Header: 'Nome'
    },
    {
      accessor: 'id',
      Header: 'Ações',
      Actions: true,
      Cell: (rowData) => {
        const { id } = rowData.row.original;
        return (
          <ActionsColumn 
            showEdit
            showDetail
            showDelete
            showPDF
            getData={getData}
            setLoaded={setLoaded}
            controller={baseController}
            deleteRoute={`Cardapio/${id}`}
            editRoute={`/cardapio/edit/${id}`}
            detailRoute={`/cardapio/detail/${id}`}
            pdfFunction={() => downloadPDF(id)}
          />
        );
      },
      cellProps:{
        style: {
          width: '100px'
        }
      }
    }
  ];

  const getData = async (controller) => {
    setData([]);
    setLoaded(false);
    
    let timeout = setTimeout(() => {
      setIsSlow(true);
    }, 5000);

    if(itemsPerPage > 0)
      baseController.get('Cardapio', {
        params: {
          page,
          itemsPerPage,
          filterValue: globalFilter
        },
        headers : {
          'Authorization': auth.token ?? JSON.parse(localStorage.getItem('auth')).token
        },
        signal: controller?.signal
      })
      .then((res) => {
        setData(res.data.cardapios);
        setTotalPages(res.data.totalPages);

        if(res.data.cardapios.length === 0)
          setMessage(emptyMessage);

        if(res.data.totalPages < page) {
          goToPage(1);
          setCurrentPage(1);
        }
      })
      .catch(() => setMessage(errorMessage))
      .finally(() => {
        setLoaded(true);
        setIsSlow(false);
        clearTimeout(timeout);
      });
  };

  const downloadPDF = (id) => {
    baseController
      .get(`Cardapio/GeneratePDF/${id}`, {
        responseType: 'arraybuffer',
        headers: {
          'Authorization': auth.token ?? JSON.parse(localStorage.getItem('auth')).token
        }
      }).then((res) => {
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', 'cardapio.pdf');

        document.body.appendChild(link);

        link.click();
        link.remove();
      }).catch(() => {
        toast.error('Erro ao gerar PDF do cardápio.', {
          theme: 'colored',
          position: 'top-right'
        });
      });
  };

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    isMounted && getData(controller);

    return () => {
      isMounted = false;
      controller.abort();
    }
  }, [page, globalFilter, itemsPerPage]);

  useEffect(() => {
    const isAuthorized = cardapioRoles.includes(auth.role ?? JSON.parse(localStorage.getItem('auth')).role);

    setAuthorized(isAuthorized);

    if(isAuthorized)
      CalculateRowQuantity(itemsPerPage, setItemsPerPage);
  }, []);

  if(!loaded) return (<FullScreenLoading isSlow={isSlow}/>);

  if(!authorized) return (<Error401 />);

  return (
    <Card className='grid-card'>
      <Card.Header>
        <h1 className='index-table-title'>
          Cardápios
        </h1>
      </Card.Header>
      <Card.Body className='table-card-body'>
        <AdvanceTableWrapper
          sortable
          pagination
          columns={columns}
          data={data ? data : []}
          perPage={itemsPerPage ? itemsPerPage : 10}
        >
          <Row className="flex-end-center mb-3">
            <Col className='grid-actions'>
              <InsertButton route={'/cardapio/edit'} />
              <ReloadButton getData={getData} />
            </Col>
            <Col xs="auto" sm={6} lg={4}>
              <AdvanceTableSearchBox 
                globalFilter={globalFilter}
                setGlobalFilter={setGlobalFilter}
                placeholder = 'Digite sua pesquisa aqui'
              />
            </Col>
          </Row>
          <AdvanceTable
            table
            rowClassName="align-middle white-space-nowrap"
            headerClassName="bg-200 text-900 text-nowrap align-middle"
            tableProps={{
              bordered: true,
              striped: true,
              className: 'fs--1 mb-0 overflow-hidden'
            }}
          />
          { !data?.length ?
            <div className='mt-3 table-no-data'>
              <p>{message}</p>
            </div>
            : <></>
          }
          <div className='table-paginator-wrapper'>
            <div className="mt-3">
              <AdvanceTablePagination
                setPage={setPage}
                goToPage={goToPage}
                pageCount={totalPages}
                pageIndex={currentPage}
                setCurrentPage={setCurrentPage}
              />
            </div>
          </div>
        </AdvanceTableWrapper>
      </Card.Body>
    </Card>
  );
}